import MatterTask from "@/components/features/matter/tasks/MatterTask";
import { Text } from "@/components/ui";
import type { MatterBreadcrumbProps } from "@/components/ui/MatterBreadcrumb.tsx/MatterBreadcrumb";
import type {
  MatterSubItemType,
  MatterTask as MatterTaskModel,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMemo, useState } from "react";
import type { MatterTaskWithType } from "./matter-task-columns";

type BoardColumnId = "todo" | "in-progress" | "complete";

type BoardColumnConfig = {
  id: BoardColumnId;
  title: string;
  helperText: string;
  matchStatuses: MatterTaskModel["status"][];
  dropStatus: MatterTaskModel["status"];
};

const BOARD_COLUMNS: BoardColumnConfig[] = [
  {
    id: "todo",
    title: "To do",
    helperText: "Fresh and upcoming work",
    matchStatuses: ["Open", ""],
    dropStatus: "Open",
  },
  {
    id: "in-progress",
    title: "In progress",
    helperText: "Actively being worked",
    matchStatuses: ["In Progress"],
    dropStatus: "In Progress",
  },
  {
    id: "complete",
    title: "Complete",
    helperText: "Done and verified",
    matchStatuses: ["Completed"],
    dropStatus: "Completed",
  },
];

const getColumnForStatus = (
  status: MatterTaskModel["status"],
): BoardColumnConfig => {
  const normalizedStatus = status || "";
  return (
    BOARD_COLUMNS.find((column) =>
      column.matchStatuses.includes(normalizedStatus),
    ) ?? BOARD_COLUMNS[0]
  );
};

type MatterTasksBoardProps = {
  tasks: MatterTaskWithType[];
  matterId: string;
  massTortId?: string;
  subItem?: { id: string; type: MatterSubItemType };
  selectedTaskIds: Set<string>;
  selectedTasks: MatterTaskWithType[];
  onToggleTaskSelection: (taskId: string) => void;
  onTaskStatusChange?: (
    task: MatterTaskWithType,
    nextStatus: MatterTaskModel["status"],
  ) => void | Promise<void>;
  canEditStatus: boolean;
  getCustomBreadcrumbItems?: (
    task: MatterTaskWithType,
  ) => MatterBreadcrumbProps["items"] | undefined;
};

export const MatterTasksBoard = ({
  tasks,
  matterId,
  massTortId,
  subItem,
  selectedTaskIds,
  selectedTasks,
  onToggleTaskSelection,
  onTaskStatusChange,
  canEditStatus,
  getCustomBreadcrumbItems,
}: MatterTasksBoardProps) => {
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const tasksByColumn = useMemo(() => {
    const initial: Record<BoardColumnId, MatterTaskWithType[]> = {
      todo: [],
      "in-progress": [],
      complete: [],
    };

    tasks.forEach((task) => {
      const column = getColumnForStatus(task.status);
      initial[column.id].push(task);
    });

    return initial;
  }, [tasks]);

  const activeTask =
    activeTaskId != null
      ? (tasks.find((task) => task.id === activeTaskId) ?? null)
      : null;

  const handleDragStart = (event: DragStartEvent) => {
    if (!canEditStatus) {
      return;
    }
    setActiveTaskId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!canEditStatus) {
      setActiveTaskId(null);
      return;
    }

    setActiveTaskId(null);
    if (!event.over) {
      return;
    }
    const overColumnId = event.over.data.current?.columnId as
      | BoardColumnId
      | undefined;
    const nextColumn = BOARD_COLUMNS.find(
      (column) => column.id === overColumnId,
    );
    if (!nextColumn) {
      return;
    }

    const movedTask = tasks.find((task) => task.id === event.active.id);
    if (!movedTask) {
      return;
    }

    if (movedTask.status === nextColumn.dropStatus) {
      return;
    }

    void onTaskStatusChange?.(movedTask, nextColumn.dropStatus);
  };

  const handleDragCancel = () => {
    setActiveTaskId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex flex-1 overflow-x-auto pb-2">
        <div className="flex min-w-[960px] flex-1 gap-4 pr-2">
          {BOARD_COLUMNS.map((column) => (
            <BoardColumn
              key={column.id}
              column={column}
              tasks={tasksByColumn[column.id]}
              matterId={matterId}
              massTortId={massTortId}
              subItem={subItem}
              selectedTaskIds={selectedTaskIds}
              selectedTasks={selectedTasks}
              onToggleTaskSelection={onToggleTaskSelection}
              canEditStatus={canEditStatus}
              getCustomBreadcrumbItems={getCustomBreadcrumbItems}
            />
          ))}
        </div>
      </div>
      <DragOverlay>
        {activeTask ? (
          <div className="w-[320px] max-w-[90vw]">
            <MatterTask
              matter={{ id: matterId }}
              massTortId={massTortId}
              subItem={subItem}
              task={activeTask}
              variant="card"
              customBreadcrumb={getCustomBreadcrumbItems?.(activeTask)}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

type BoardColumnProps = {
  column: BoardColumnConfig;
  tasks: MatterTaskWithType[];
  matterId: string;
  massTortId?: string;
  subItem?: { id: string; type: MatterSubItemType };
  selectedTaskIds: Set<string>;
  selectedTasks: MatterTaskWithType[];
  onToggleTaskSelection: (taskId: string) => void;
  canEditStatus: boolean;
  getCustomBreadcrumbItems?: (
    task: MatterTaskWithType,
  ) => MatterBreadcrumbProps["items"] | undefined;
};

const BoardColumn = ({
  column,
  tasks,
  matterId,
  massTortId,
  subItem,
  selectedTaskIds,
  selectedTasks,
  onToggleTaskSelection,
  canEditStatus,
  getCustomBreadcrumbItems,
}: BoardColumnProps) => {
  const droppableId = `column-${column.id}`;
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: { type: "column", columnId: column.id },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "bg-background/80 flex h-full min-h-[400px] min-w-[320px] flex-1 flex-col rounded-lg border p-4",
        isOver && canEditStatus && "border-primary/60 shadow-md",
      )}
    >
      <div className="border-border/70 mb-3 border-b pb-2">
        <Text
          variant="caption"
          color="muted"
          className="font-semibold tracking-wider uppercase"
        >
          {column.title}
        </Text>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto pr-1">
        <SortableContext
          items={tasks.map((task) => task.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.length === 0 && (
            <div
              className={cn(
                "border-border/60 flex flex-1 items-center justify-center rounded-md border border-dashed p-4 text-center",
                canEditStatus
                  ? "text-muted-foreground"
                  : "text-muted-foreground/80",
              )}
            >
              <Text variant="subHeading">
                {canEditStatus
                  ? "Drag tasks here to add them to this column."
                  : "No tasks in this column."}
              </Text>
            </div>
          )}
          {tasks.map((task) => (
            <BoardTaskCard
              key={task.id}
              task={task}
              matterId={matterId}
              massTortId={massTortId}
              subItem={subItem}
              columnId={column.id}
              isSelected={selectedTaskIds.has(task.id)}
              selectedTasks={selectedTasks}
              onToggleTaskSelection={onToggleTaskSelection}
              isDraggable={canEditStatus}
              getCustomBreadcrumbItems={getCustomBreadcrumbItems}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
};

type BoardTaskCardProps = {
  task: MatterTaskWithType;
  matterId: string;
  massTortId?: string;
  subItem?: { id: string; type: MatterSubItemType };
  columnId: BoardColumnId;
  isSelected: boolean;
  selectedTasks: MatterTaskWithType[];
  onToggleTaskSelection: (taskId: string) => void;
  isDraggable: boolean;
  getCustomBreadcrumbItems?: (
    task: MatterTaskWithType,
  ) => MatterBreadcrumbProps["items"] | undefined;
};

const BoardTaskCard = ({
  task,
  matterId,
  massTortId,
  subItem,
  columnId,
  isSelected,
  selectedTasks,
  onToggleTaskSelection,
  isDraggable,
  getCustomBreadcrumbItems,
}: BoardTaskCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: { type: "task", columnId },
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "cursor-grab active:cursor-grabbing",
        isDraggable ? "touch-none" : "cursor-default",
      )}
    >
      <MatterTask
        task={task}
        matter={{ id: matterId }}
        massTortId={massTortId}
        subItem={subItem}
        variant="card"
        isSelected={isSelected}
        onToggleSelect={onToggleTaskSelection}
        selectedTasks={selectedTasks}
        customBreadcrumb={getCustomBreadcrumbItems?.(task)}
      />
      {isDragging && (
        <div
          className="mt-2 rounded-md border border-dashed"
          aria-hidden="true"
        />
      )}
    </div>
  );
};
